'use strict'
import { Drawer, IconButton } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import { buildFrequencyLabel } from './scheduleUtils'

/* Sync issue codes → human labels (mirrors RoutingPropertiesTable). */
const SYNC_ISSUE_LABELS = {
  'no-estimate-with-activity': 'Has visits but no internal estimate',
  'no-board-visits': 'Future tickets not on schedule board',
  'missing-geo': 'No valid coordinates',
  'no-crew-linkage': 'No crew size resolved from visits',
  'stale-not-processed': 'Not reached by last sync'
}

/* ── Formatting helpers ─────────────────────────────────────────── */
const fmtHoursFromMinutes = (minutes) => {
  const m = Math.round(Number(minutes) || 0)
  if (!m) return null
  const h = Math.floor(m / 60)
  const rem = m % 60
  if (!h) return `${rem}m`
  if (!rem) return `${h}h`
  return `${h}h ${rem}m`
}

const fmtHours = (hours) => {
  const h = Number(hours) || 0
  if (!h) return null
  return `${h.toFixed(1)}h`
}

const isFilled = (value) => value !== null && value !== undefined && value !== '' && value !== 0

/* ── Layout atoms ───────────────────────────────────────────────── */
const SectionLabel = ({ children }) => (
  <div style={{
    fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    margin: '18px 0 8px'
  }}>{children}</div>
)

const Row = ({ label, value }) => {
  if (!isFilled(value)) return null
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', fontSize: 12 }}>
      <span style={{ color: 'var(--df-ink-4)' }}>{label}</span>
      <span style={{ color: 'var(--df-ink)', fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  )
}

export default function PropertyQuickDetails ({ open, onClose, property }) {
  const row = property || {}
  const aspireUrl = row.aspirePropertyId
    ? `https://cloud.youraspire.com/app/properties/overview/${row.aspirePropertyId}`
    : null
  const locationLine = [row.address, row.city].filter(Boolean).join(', ')

  const estOnSite = fmtHoursFromMinutes(row.estimatedMinutesOnSite)
  const manHours = isFilled(row.internalEstimatedManHours) ? `${Number(row.internalEstimatedManHours).toFixed(1)}h` : null
  const median = fmtHoursFromMinutes(row.medianOnSiteMinutes) || fmtHours(row.medianOnSiteHours)
  const avgOnSite = fmtHoursFromMinutes(row.avgOnSiteMinutes)
  const drive = fmtHours(row.aspireDriveManHours12mo)

  const syncIssues = row.syncHealth?.status === 'problem'
    ? (row.syncHealth.issues || []).map(code => SYNC_ISSUE_LABELS[code] || code)
    : []

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div style={{
        width: 340, padding: '18px 20px', boxSizing: 'border-box',
        background: 'var(--df-surface)', color: 'var(--df-ink)',
        fontFamily: 'inherit', minHeight: '100%'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--df-ink)' }}>
              {row.propertyName || 'Property'}
            </div>
            {isFilled(row.aspirePropertyId) && (
              <div style={{ fontSize: 11, color: 'var(--df-ink-4)', marginTop: 2 }}>#{row.aspirePropertyId}</div>
            )}
            {locationLine && (
              <div style={{ fontSize: 12, color: 'var(--df-ink-2)', marginTop: 4 }}>{locationLine}</div>
            )}
            {aspireUrl && (
              <a
                href={aspireUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8,
                  fontSize: 12, fontWeight: 600, color: 'var(--df-primary)', textDecoration: 'none'
                }}
              >
                Open in Aspire <OpenInNewIcon style={{ fontSize: 13 }} />
              </a>
            )}
          </div>
          <IconButton size="small" onClick={onClose} aria-label="Close details">
            <CloseIcon style={{ fontSize: 18, color: 'var(--df-ink-4)' }} />
          </IconButton>
        </div>

        {/* Scheduling */}
        <SectionLabel>Scheduling</SectionLabel>
        <Row label="Frequency" value={buildFrequencyLabel(row)} />
        <Row label="Assigned crew" value={row.assignedCrewName} />
        <Row label="Assigned route" value={row.assignedRouteName} />
        <Row label="Locked to" value={row.lockedRouteName || row.lockedCrewName} />
        <Row label="Day" value={row.preferredDay} />
        <Row label="Days" value={(row.preferredDays || []).join(', ')} />
        <Row label="Week type" value={row.weekType ? `Week ${row.weekType}` : null} />
        <Row label="VIP" value={row.isVip ? 'Yes' : null} />

        {/* Workload */}
        <SectionLabel>Workload</SectionLabel>
        <Row label="Internal man-hours" value={manHours} />
        <Row label="Crew count" value={row.internalEstimateCrewCount || null} />
        <Row label="Est. on-site" value={estOnSite} />
        <Row label="Median on-site" value={median} />
        <Row label="Avg on-site" value={avgOnSite} />
        <Row label="Aspire drive (12mo)" value={drive} />

        {/* Sync health */}
        {syncIssues.length > 0 && (
          <>
            <SectionLabel>Sync health</SectionLabel>
            <div style={{
              background: 'rgba(169,52,29,0.08)', border: '1px solid rgba(169,52,29,0.25)',
              borderRadius: 6, padding: '8px 10px'
            }}>
              {syncIssues.map((issue, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--df-danger, #a9341d)', padding: '2px 0' }}>
                  • {issue}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Drawer>
  )
}
